import { rdProjectCategoryLabel, SelectedRdProjectCategory } from '@common/neo.tax';
import { BadgeColorVariant } from '@ui-components/neo.tax';

import { ProjectRdDetermination } from '@neo.tax/shared';

import { FIRST_TAX_YEAR_WITHOUT_174 } from '../constants';

export type ProjectBadgeConfig = {
  label: string;
  colorVariant: BadgeColorVariant;
  description?: string;
  sortOrdinal: number;
};

const rdCategoryBadgeConfigMap: Record<SelectedRdProjectCategory, Omit<ProjectBadgeConfig, 'description' | 'label'>> = {
  RD_CREDIT: {
    colorVariant: 'default',
    sortOrdinal: 1,
  },
  SECTION_174_ONLY: {
    colorVariant: 'default',
    sortOrdinal: 2,
  },
  EXCLUDED: {
    colorVariant: 'default',
    sortOrdinal: 3,
  },
} as const;

export const getRdCategoryBadgeConfig = ({
  consideredForRd,
  isSection174Eligible,
  taxYear,
}: {
  consideredForRd: boolean;
  isSection174Eligible: boolean;
  taxYear?: number;
}): ProjectBadgeConfig => {
  if (consideredForRd) {
    return {
      ...rdCategoryBadgeConfigMap.RD_CREDIT,
      label: rdProjectCategoryLabel[SelectedRdProjectCategory.RD_CREDIT],
    };
  }

  // §174 Only category is meaningless for TY2025+ — collapse into "Not Qualified"
  if (taxYear !== undefined && taxYear >= FIRST_TAX_YEAR_WITHOUT_174) {
    return {
      ...rdCategoryBadgeConfigMap.EXCLUDED,
      label: 'Not Qualified',
    };
  }

  if (isSection174Eligible) {
    return {
      ...rdCategoryBadgeConfigMap.SECTION_174_ONLY,
      label: rdProjectCategoryLabel[SelectedRdProjectCategory.SECTION_174_ONLY],
    };
  }

  return {
    ...rdCategoryBadgeConfigMap.EXCLUDED,
    label: rdProjectCategoryLabel[SelectedRdProjectCategory.EXCLUDED],
  };
};

const rdDeterminationBadgeConfigMap: Record<string, Omit<ProjectBadgeConfig, 'description'>> = {
  RD_CREDIT_ELIGIBLE: {
    colorVariant: 'green',
    label: 'R&D Credit Eligible',
    sortOrdinal: 1,
  },
  NEEDS_REVIEW: {
    colorVariant: 'yellow',
    label: 'Needs Review',
    sortOrdinal: 2,
  },
  NOT_QUALIFIED: {
    colorVariant: 'red',
    label: 'Not Qualified',
    sortOrdinal: 3,
  },
} as const;

/**
 * Maps the backend aiGuidance field to proper badge configuration
 * This is used when we have the pre-calculated aiGuidance value from the backend
 */
export const getAiGuidanceBadgeConfig = (
  aiGuidance: string | null | undefined,
  rdDetermination?: Pick<ProjectRdDetermination, 'qualifiedPurposeReasoning'> | null,
): ProjectBadgeConfig | undefined => {
  if (!aiGuidance) return undefined;

  const badgeConfigMap: Record<string, Omit<ProjectBadgeConfig, 'description'>> = {
    'R&D Credit Eligible': {
      ...rdDeterminationBadgeConfigMap.RD_CREDIT_ELIGIBLE,
    },
    'Needs Review': {
      ...rdDeterminationBadgeConfigMap.NEEDS_REVIEW,
    },
    'Not Qualified': {
      ...rdDeterminationBadgeConfigMap.NOT_QUALIFIED,
    },
  };

  const config = badgeConfigMap[aiGuidance];
  if (!config) return undefined;

  return {
    ...config,
    description: rdDetermination?.qualifiedPurposeReasoning || '',
  };
};
